import { Link, router } from '@inertiajs/react';
import { LogOut, Menu, Search, Settings, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Avatar, AvatarFallback } from '../ui/Avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/DropdownMenu';
import { IconButton } from '../ui/IconButton';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '../ui/Tooltip';
import type { NexusUser } from '../../types';
import { cn } from '../ui/utils';
import GlobalSearch from './GlobalSearch';

/** Rota legada de logoff do ERP (`routes/web.php` → prefix `login`). */
const LOGOUT_URL = '/login/logoff';
const LAYOUT_URL = '/layout';

type AppHeaderProps = {
    user: NexusUser | null;
    onAbout: () => void;
    onLogout?: () => void;
    showMenuButton?: boolean;
    menuOpen?: boolean;
    onMenuClick?: () => void;
    /** Exibe marca no header (modo menu superior no desktop). */
    showBrand?: boolean;
    /** Navegação horizontal (modo menu superior). */
    topNav?: ReactNode;
};

function userInitials(nome: string | null | undefined): string {
    if (!nome?.trim()) {
        return 'AD';
    }

    const parts = nome.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }

    return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
}

function userName(user: NexusUser | null): string | null | undefined {
    return user?.nome?.trim() || user?.name?.trim();
}

function firstName(nome: string | null | undefined): string {
    if (!nome?.trim()) {
        return 'Administrador';
    }
    return nome.trim().split(/\s+/)[0] ?? 'Administrador';
}

export function getDisplayName(user: NexusUser | null): string {
    return userName(user) || 'Administrador';
}

export function getRoleLabel(user: NexusUser | null): string {
    if (user?.adm) {
        return 'Administrador';
    }
    return user ? 'Usuário' : 'Administrador';
}

export { firstName };

function goToLayout(): void {
    router.visit(LAYOUT_URL);
}

export default function AppHeader({
    user,
    onAbout,
    onLogout,
    showMenuButton = false,
    menuOpen = false,
    onMenuClick,
    showBrand = false,
    topNav = null,
}: AppHeaderProps) {
    const [searchOpen, setSearchOpen] = useState(false);
    const displayName = getDisplayName(user);
    const role = getRoleLabel(user);
    const empresaLabel = user?.empresa_nome
        ? user.empresa_nome
        : user?.empresa_id
          ? `Empresa #${user.empresa_id}`
          : 'Empresa não informada';
    const hasTopNav = Boolean(topNav);
    const kbdHint = showMenuButton ? undefined : 'Ctrl + K';

    return (
        <>
            <header
                className={cn(
                    'nx-header',
                    hasTopNav && 'nx-header--top-nav',
                    showBrand && 'nx-header--with-brand',
                )}
            >
                {showMenuButton ? (
                    <IconButton
                        className="nx-menu-toggle"
                        size="md"
                        variant="ghost"
                        label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
                        aria-expanded={menuOpen}
                        aria-controls="nx-sidebar"
                        onClick={onMenuClick}
                    >
                        {menuOpen ? (
                            <X size={20} strokeWidth={2} />
                        ) : (
                            <Menu size={20} strokeWidth={2} />
                        )}
                    </IconButton>
                ) : null}

                {showBrand ? (
                    <div className="nx-header-brand">
                        <img
                            src="/logo_infor.png"
                            alt="Infor"
                            className="nx-brand-logo"
                            width={28}
                            height={28}
                        />
                        <span className="nx-header-brand-text">SNGPC WEB INFOR</span>
                    </div>
                ) : null}

                {hasTopNav ? (
                    <div className="nx-header-nav">{topNav}</div>
                ) : (
                    <button
                        type="button"
                        className="nx-search-trigger nx-global-search"
                        onClick={() => setSearchOpen(true)}
                        aria-haspopup="dialog"
                        aria-expanded={searchOpen}
                        aria-label="Buscar no sistema"
                    >
                        <Search size={16} strokeWidth={2} aria-hidden />
                        <span className="nx-search-trigger-label">
                            Buscar no sistema…
                        </span>
                        {kbdHint ? <kbd>{kbdHint}</kbd> : null}
                    </button>
                )}

                <div className="nx-header-actions">
                    {hasTopNav ? (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <IconButton
                                    size="md"
                                    label="Buscar no sistema (Ctrl + K)"
                                    onClick={() => setSearchOpen(true)}
                                    aria-haspopup="dialog"
                                    aria-expanded={searchOpen}
                                >
                                    <Search size={17} strokeWidth={1.9} />
                                </IconButton>
                            </TooltipTrigger>
                            <TooltipContent>Buscar (Ctrl + K)</TooltipContent>
                        </Tooltip>
                    ) : null}

                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Link
                                href={LAYOUT_URL}
                                className="nx-icon-btn nx-icon-btn--md"
                                aria-label="Configurações de layout"
                                prefetch={false}
                            >
                                <Settings size={17} strokeWidth={1.9} />
                            </Link>
                        </TooltipTrigger>
                        <TooltipContent>Configurações de layout</TooltipContent>
                    </Tooltip>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                type="button"
                                className="nx-user-trigger"
                                aria-label="Menu do usuário"
                            >
                                <Avatar>
                                    <AvatarFallback>
                                        {userInitials(displayName)}
                                    </AvatarFallback>
                                </Avatar>
                                <span className="nx-user">
                                    <span
                                        className="nx-user-name"
                                        title={displayName}
                                        aria-label={displayName}
                                    >
                                        {displayName}
                                    </span>
                                    <span className="nx-user-meta">{role}</span>
                                </span>
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" sideOffset={8}>
                            <DropdownMenuLabel>Conta conectada</DropdownMenuLabel>
                            <div className="nexus-dropdown-meta">
                                <strong>{displayName}</strong>
                                <span>{role}</span>
                                <span>{empresaLabel}</span>
                                {user?.login ? <span>Login: {user.login}</span> : null}
                            </div>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onSelect={goToLayout}>
                                Preferências de layout
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={onAbout}>
                                Sobre o Infor Nexus
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                className="nx-dropdown-item--danger"
                                onSelect={onLogout ?? (() => { window.location.href = LOGOUT_URL; })}
                            >
                                <LogOut size={15} strokeWidth={2} aria-hidden />
                                Sair
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </header>

            <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
        </>
    );
}
